import { FC, useState, MutableRefObject, useEffect } from 'react';
import { Box, TextField, InputAdornment } from '@mui/material';
import {
  gridPreferencePanelStateSelector,
  GridPreferencePanelsValue,
  GridFilterListIcon,
  GridViewColumnIcon,
} from '@mui/x-data-grid';
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import SearchIcon from '@mui/icons-material/Search';
import TableRowsIcon from '@mui/icons-material/TableRows';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';

import { useDataGrid } from '@carcareers/contexts';
import { DataGridTool } from './DataGridTool';
import { DataGridActions } from './DataGridAction';
interface DataGridToolbarProps {
  apiRef: MutableRefObject<GridApiCommunity>;
  actions?: Partial<DataGridActions>;
  tooltipAdd?: string;
  tooltipColumns?: string;
  tooltipDensity?: string;
  tooltipFilters?: string;
  tooltipExport?: string;
  tooltipSearch?: string;
  showAddButton?: boolean;
}

export const DataGridToolbar: FC<DataGridToolbarProps> = ({
  apiRef,
  actions,
  tooltipAdd = 'Add',
  tooltipColumns = 'Manage Columns',
  tooltipDensity = 'Row Size',
  tooltipFilters = 'Filter',
  tooltipExport = 'Export to CSV',
  tooltipSearch = 'Search',
  showAddButton = true,
}) => {
  const { searchData, currentSearchTerm, isSearchMode } = useDataGrid();
  const [searchTerm, setSearchTerm] = useState(currentSearchTerm);
  const [density, setDensity] = useState<'compact' | 'standard' | 'comfortable'>('standard');

  const togglePreferencePanel = (preference: string) => {
    const preferencePanel = gridPreferencePanelStateSelector(apiRef.current.state);
    if (preferencePanel.open) {
      apiRef.current.hidePreferences();
    } else {
      if (preference === 'columns') {
        apiRef.current.showPreferences(GridPreferencePanelsValue.columns);
      } else if (preference === 'filters') {
        apiRef.current.showFilterPanel();
      } else if (preference === 'density') {
        if (density === 'compact') {
          apiRef.current.setDensity('standard');
          setDensity('standard');
        } else if (density === 'standard') {
          apiRef.current.setDensity('comfortable');
          setDensity('comfortable');
        } else {
          apiRef.current.setDensity('compact');
          setDensity('compact');
        }
      } else if (preference === 'export') {
        apiRef.current.exportDataAsCsv();
      }
    }
  };

  const handleSearch = () => searchData({ q: searchTerm });

  const handleClearSearch = () => {
    setSearchTerm('');
    searchData({ q: '' });
  };

  // Sync local search term with context when it changes (e.g., when refresh is called)
  useEffect(() => {
    setSearchTerm(currentSearchTerm);
  }, [currentSearchTerm]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      {actions?.add && (
        <>
          <DataGridTool
            onClick={() => actions?.add?.onClick()}
            tooltip={tooltipAdd}
            icon={<AddIcon />}
          />
          <Box
            sx={{
              height: 32,
              width: '1px',
              backgroundColor: 'divider',
              mx: 2,
            }}
          />
        </>
      )}
      <DataGridTool
        onClick={() => togglePreferencePanel('columns')}
        tooltip={tooltipColumns}
        icon={<GridViewColumnIcon />}
        isIconButton
      />
      <DataGridTool
        onClick={() => togglePreferencePanel('density')}
        tooltip={tooltipDensity}
        icon={<TableRowsIcon />}
        isIconButton
      />
      <DataGridTool
        onClick={() => togglePreferencePanel('filters')}
        tooltip={tooltipFilters}
        icon={<GridFilterListIcon />}
        isIconButton
      />
      <DataGridTool
        onClick={() => togglePreferencePanel('export')}
        tooltip={tooltipExport}
        icon={<SaveAltIcon />}
        isIconButton
      />
      <Box
        sx={{
          height: 32,
          width: '1px',
          backgroundColor: 'divider',
          mx: 2,
        }}
      />
      <TextField
        label={tooltipSearch}
        variant="outlined"
        size="small"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ width: '300px', mr: 1 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: isSearchMode && searchTerm ? (
            <InputAdornment position="end">
              <Box
                component="button"
                type="button"
                onClick={handleClearSearch}
                sx={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'text.primary',
                  },
                }}
              >
                <ClearIcon fontSize="small" />
              </Box>
            </InputAdornment>
          ) : null,
        }}
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            handleSearch();
          }
        }}
      />
      <DataGridTool onClick={handleSearch} label={tooltipSearch} variant="outlined" />
    </Box>
  );
};

export default DataGridToolbar;
